import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { appLogger } from '../../../utils/datadog';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { GreenroomState as GRS } from '../../../stores/types/types';
import useShots from '../../../contexts/shots/provider';
import { ROOM_STATES } from '../../../stores/reducers/settings';

interface Props {
	paused: boolean;
	videoPlayPause: (paused: boolean) => void;
	disabled?: boolean;
	className?: string;
	isForShotEditorModal?: boolean;
}

export default function PlayButton(props: Props) {
	const {
		paused,
		videoPlayPause,
		disabled = false,
		className = '',
		isForShotEditorModal = false,
	} = props;

	const shotStartTimestampEntity = useSelector((s: GRS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);

	const {
		currentShot: { shot_id: shotId },
		handleSelectShot,
	} = useShots();

	const shouldPlayShot = useMemo(() => {
		if (
			isForShotEditorModal
			|| roomState === ROOM_STATES.EDITING
			|| shotStartTimestampEntity
		) {
			return false;
		}
		return true;
	}, [roomState, shotStartTimestampEntity, isForShotEditorModal]);

	const buttonColor = useMemo(() => {
		if (disabled) {
			return COLORS.TEXT_GRAY_100_DISABLED;
		}
		if (shouldPlayShot) {
			return COLORS.TEXT_GRAY_1000;
		}
		return COLORS.WHITE;
	}, [disabled, shouldPlayShot]);

	return (
		<button
			onClick={(e) => {
				appLogger.debug(`feed-media.tsx ${paused ? 'PLAY' : 'PAUSE'} play button`, { doFireSync: true });
				if (disabled) return;
				if (shouldPlayShot) {
					handleSelectShot(shotId, true);
				} else {
					videoPlayPause(!paused);
				}
				e.currentTarget.blur();
			}}
			onMouseLeave={(e) => {
				e.currentTarget.blur();
			}}
			className={`
				play-button
				round
				${disabled ? 'disabled' : `background-${shouldPlayShot ? 'green' : 'lighter'}`}
				${className}
			`}
		>
			<Icon
				name={paused ? ICONS.PLAY : ICONS.PAUSE}
				size={14}
				color={buttonColor} />
		</button>
	);
}
